import React, { useState } from "react";
import Tesseract from "tesseract.js";

// Interface para os itens de nome
interface NameItem {
  id: number;
  name: string;
  completed: boolean;
}

const NameListAndRecognition: React.FC = () => {
  const [names, setNames] = useState<NameItem[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [recognizedText, setRecognizedText] = useState<string>("");
  const [imageText, setImageText] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
 
  // Adicionar nome à lista
  const addName = () => {
    if (inputValue.trim()) {
      setNames((prevNames) => [
        ...prevNames,
        { id: Date.now(), name: inputValue.trim(), completed: false },
      ]);
      setInputValue(""); // Limpar o campo de entrada
    }
  };

  // Alternar status de concluído
  const toggleCompleted = (id: number) => {
    setNames((prevNames) =>
      prevNames.map((item) =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  // Iniciar o reconhecimento de voz
  const startVoiceRecognition = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Seu navegador não suporta reconhecimento de voz.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "pt-BR"; // Defina o idioma conforme necessário
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setRecognizedText(transcript);
      addName(); // Adiciona o texto reconhecido como nome
    };
    recognition.onerror = () => {
      alert("Erro no reconhecimento de voz.");
    };
    recognition.start();
  };

  // Extrair texto da imagem usando Tesseract
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      extractTextFromImage(file);
    }
  };

  const extractTextFromImage = async (file: File) => {
    setLoading(true);
    try {
      const result = await Tesseract.recognize(file, "por", {
        logger: () => {},
      });
    
      setImageText(result.data.text);
      addName(); // Adiciona o texto extraído da imagem como nome
    } catch (error) {
      console.error("Erro ao extrair texto da imagem:", error);
      alert("Não foi possível extrair o texto da imagem.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-100 p-4">
      <h1 className="text-2xl font-bold mb-4">Gerenciador de Nomes e Reconhecimento</h1>

      {/* Campo de entrada e botão para adicionar nomes */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Digite um nome"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="border rounded px-2 py-1 mr-2"
        />
        <button
          onClick={addName}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Adicionar Nome
        </button>
      </div>

      {/* Lista de nomes com checkboxes */}
      {names.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold">Lista de Nomes</h2>
          <ul className="list-none mt-2">
            {names.map((item) => (
              <li key={item.id} className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleCompleted(item.id)}
                  className="form-checkbox"
                />
                <span
                  className={`${
                    item.completed ? "line-through text-gray-500" : ""
                  }`}
                >
                  {item.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reconhecimento de voz */}
      <div className="mb-6 mt-6">
        <h2 className="text-lg font-semibold">Reconhecimento de Voz</h2>
        <button
          onClick={startVoiceRecognition}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Iniciar Reconhecimento de Voz
        </button>
        {recognizedText && (
          <p className="mt-2 bg-gray-200 p-2 rounded">
            Texto Reconhecido: <strong>{recognizedText}</strong>
          </p>
        )}
      </div>

      {/* Extração de texto de imagem */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Extração de Texto de Imagem</h2>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="mb-4"
        />
        {loading && <p>Extraindo texto da imagem...</p>}
        {imageText && (
          <p className="mt-2 bg-gray-200 p-2 rounded">
            Texto Extraído: <strong>{imageText}</strong>
          </p>
        )}
      </div>

      {/* Visualização da Imagem */}
      {imageFile && (
        <div className="mt-4">
          <h3 className="text-md font-medium">Imagem Selecionada:</h3>
          <img
            src={URL.createObjectURL(imageFile)}
            alt="Imagem selecionada"
            className="mt-2 max-w-xs border"
          />
        </div>
      )}
    </div>
  );
};

export default NameListAndRecognition;
